import * as Sentry from '@sentry/electron/renderer';
import Button from '@shared/components/core/button/CoreButton';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { getBillingRedirectDecision, resolveBillingLink } from './billingRedirectDecision';

export default function OrgBillingRedirectContent() {
    const { t } = useTranslation();
    const { organizationId, productId, managedOrgId } = useParams();
    const navigate = useNavigate();

    const openBillingLink = (billingLink: string | null, errorRedirect: string) => {
        if (billingLink) {
            window.open(billingLink, '_blank', 'noopener,noreferrer');
            navigate(-1); // redirect to previous page to maintain context
            useAlertsStore.getState().showSuccess(t('common:license.redirect.external'));
        } else {
            Sentry.captureMessage('Failed to resolve billing link, showing fallback', {
                extra: {
                    organizationId,
                    productId,
                    managedOrgId,
                    billingLink: billingLink,
                    errorRedirect: errorRedirect,
                    userLicenseAccess: JSON.stringify(useCurrentUserStore.getState().userLicenseAccess),
                },
            });
            navigate(errorRedirect, { replace: true });
        }
    };

    const loadBillingLink = async () => {
        if (!organizationId) return false;

        const organizationStore = useOrganizationStore.getState();
        let organizationMembership = organizationStore.getOrganizationMembership(organizationId);
        if (!organizationMembership) {
            await organizationStore.loadOrganizations(false);
            organizationMembership = useOrganizationStore.getState().getOrganizationMembership(organizationId);
        }
        if (!organizationMembership) return false;

        const decision = getBillingRedirectDecision({
            organizationId,
            organizationMembership,
            managedOrgId,
            productId,
        });

        if (decision.type === 'permissionDenied') {
            return false;
        }

        if (decision.type === 'navigateToCompare') {
            navigate(decision.comparePath, { replace: true });
            return true;
        }

        const billingLink = await resolveBillingLink(useOrganizationStore.getState(), decision.request);
        openBillingLink(billingLink, decision.errorRedirectPath);
        return true;
    };

    useEffect(() => {
        const manageBillingLink = async () => {
            const hasBillingPermissions = await loadBillingLink();
            if (!hasBillingPermissions) {
                useAlertsStore.getState().showError(t('common:organization.canManageBilling.error'));
                navigate(-1);
            }
        };

        manageBillingLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    return (
        <Button startIcon="repeat" onClick={() => loadBillingLink()}>
            {t('common:reload')}
        </Button>
    );
}
